// Libraries imports
import { useState } from 'react';
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Grid";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';

// Assets and theme imports
import { ReactComponent as DeleteIcon } from "../../../assets/images/delete1.svg";
import { ReactComponent as EditIcon } from "../../../assets/images/edit.svg";
import { ReactComponent as UserRoleIcon } from "../../../assets/images/user_role.svg";

import { THEME_MODE } from "../../../constants/Theme";
import { useLockUser, useUnlockUser } from './services';
import { LockUserDialog } from './LockUserDialog';
import { UnlockUserDialog } from './UnlockUserDialog';
import { LockInfoPopover } from './LockInfoPopover';
import { formatLastLogin } from './utils/dateFormatter';

const table_header_list = ["Username", "Email", "Phone Number", "User Role", "Status", "Last Login"];

type ThemeMode = typeof THEME_MODE[keyof typeof THEME_MODE];

interface User {
  _idUser: string;
  name: string;
  email: string;
  phoneNumber: string;
  userRole: string;
  isLocked: boolean;
  lockedReason?: string;
  lockedAt?: string;
  lastLoginAt?: string;
}

interface UserTableListProps {
  themeMode: string;
  itemList: User[];
  editItem: (item: User) => void;
  deleteItem: (item: User) => void;
  changeRole: (item: User) => void;
  onLockStatusChange?: () => void;
}

const UserTableList = ({
  themeMode,
  itemList,
  editItem,
  deleteItem,
  changeRole,
  onLockStatusChange
}: UserTableListProps) => {
  const theme_mode = THEME_MODE[(themeMode ?? "light") as keyof typeof THEME_MODE];
  const styles = componentStyles(theme_mode);

  const [lockDialogOpen, setLockDialogOpen] = useState(false);
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  const lockUserMutation = useLockUser();
  const unlockUserMutation = useUnlockUser();

  const handleLockClick = (user: User) => {
    setSelectedUser(user);
    setLockDialogOpen(true);
  };

  const handleUnlockClick = (user: User) => {
    setSelectedUser(user);
    setUnlockDialogOpen(true);
  };

  const handleLockConfirm = async (reason?: string) => {
    if (!selectedUser) return;

    lockUserMutation.mutate(
      { userId: selectedUser._idUser, reason },
      {
        onSuccess: () => {
          setLockDialogOpen(false);
          setSelectedUser(null);
          onLockStatusChange?.();
        },
      }
    );
  };

  const handleUnlockConfirm = async () => {
    if (!selectedUser) return;

    unlockUserMutation.mutate(selectedUser._idUser, {
      onSuccess: () => {
        setUnlockDialogOpen(false);
        setSelectedUser(null);
        onLockStatusChange?.();
      },
    });
  };

  const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
      backgroundColor: theme.palette.common.black,
      color: theme.palette.common.white,
      fontFamily: "GilroySemiBold",
    },
    [`&.${tableCellClasses.body}`]: {
      fontSize: 14,
      fontFamily: "GilroyMedium",
    },
  }));

  const StyledTableRow = styled(TableRow)(({ theme }) => ({
    "&:nth-of-type(odd)": {
      backgroundColor: theme.palette.action.hover,
    },
    // hide last border
    "&:last-child td, &:last-child th": {
      border: 0,
    },
  }));

  return (
    <>
      <TableContainer component={Paper}>
        {itemList.length ? (
          <Grid container direction="row">
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 700 }} aria-label="customized table">
                <TableHead>
                  <TableRow>
                    {table_header_list.map((x, i) => {
                      return (
                        <StyledTableCell key={i}>
                          <Box>{x}</Box>
                        </StyledTableCell>
                      );
                    })}
                    <StyledTableCell></StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itemList.map((item, index) => (
                    <StyledTableRow key={index}>
                      <StyledTableCell>
                        <Box sx={styles.nameBox}>{item.name}</Box>
                      </StyledTableCell>
                      <StyledTableCell>{item.email}</StyledTableCell>
                      <StyledTableCell>{item.phoneNumber}</StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{ textTransform: "capitalize" }}>
                          {item.userRole}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box display="flex" alignItems="center">
                          {item.isLocked ? (
                            <>
                              <Chip
                                label="Locked"
                                size="small"
                                sx={{
                                  bgcolor: '#ffebee',
                                  color: '#c62828',
                                  fontFamily: 'GilroySemiBold',
                                  fontSize: 12,
                                }}
                              />
                              <LockInfoPopover
                                lockedReason={item.lockedReason}
                                lockedAt={item.lockedAt}
                                lastLoginAt={item.lastLoginAt}
                              />
                            </>
                          ) : (
                            <Chip
                              label="Active"
                              size="small"
                              sx={{
                                bgcolor: '#e8f5e9',
                                color: '#2e7d32',
                                fontFamily: 'GilroySemiBold',
                                fontSize: 12,
                              }}
                            />
                          )}
                        </Box>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Typography variant="body2" fontFamily="GilroyMedium" color="text.secondary">
                          {formatLastLogin(item.lastLoginAt)}
                        </Typography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <Box sx={styles.actionBox} pr={2}>
                          {item.isLocked ? (
                            <Tooltip title="Unlock User" placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleUnlockClick(item)}
                                sx={{ mr: 1 }}
                              >
                                <LockOpenIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Tooltip title="Lock User" placement="top">
                              <IconButton
                                size="small"
                                onClick={() => handleLockClick(item)}
                                sx={{ mr: 1 }}
                              >
                                <LockIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                          <EditIcon
                            height={24}
                            width={24}
                            style={{
                              cursor: "pointer",
                              marginRight: "1rem",
                              strokeWidth: "0.1px",
                            }}
                            onClick={() => editItem(item)}
                          />
                          <UserRoleIcon
                            height={24}
                            width={24}
                            style={{
                              cursor: "pointer",
                              marginRight: "1rem",
                              fill: theme_mode.iconBg2,
                            }}
                            onClick={() => changeRole(item)}
                          />
                          <DeleteIcon
                            width={24}
                            height={24}
                            style={{ cursor: "pointer" }}
                            onClick={() => deleteItem(item)}
                          />
                        </Box>
                      </StyledTableCell>
                    </StyledTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>
        ) : null}
      </TableContainer>

      <LockUserDialog
        open={lockDialogOpen}
        onClose={() => {
          setLockDialogOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleLockConfirm}
        userName={selectedUser?.name || ''}
        loading={lockUserMutation.isPending}
      />

      <UnlockUserDialog
        open={unlockDialogOpen}
        onClose={() => {
          setUnlockDialogOpen(false);
          setSelectedUser(null);
        }}
        onConfirm={handleUnlockConfirm}
        userName={selectedUser?.name || ''}
        loading={unlockUserMutation.isPending}
      />
    </>
  );
};

export default UserTableList;

const componentStyles = (Theme: ThemeMode) => ({
  nameBox: {
    minWidth: 110,
  },
  actionBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    path: {
      stroke: Theme.iconBg2,
    },
  },
});
